import React, { useState } from 'react';
import {
    EyeOutlined,
    EyeInvisibleOutlined,
    CopyOutlined
} from "@ant-design/icons";
import { message, Tag } from "antd";
import { Badge } from "reactstrap";
import { formatDateTime, copyToClipboard } from '../../../../utils/functions';
import CommonTable from '../../../global/table';
import NoRecordFound from '../../../global/noRecordFound';
import CommonCardView from '../../../global/cardView';
import EnhanceZapierCardView from './enhanceZapierCardView';
import CommonDrawer from '../../../global/drawer';
import EnhanceZapierDetails from './enhanceZapierDetails';

const EnhancedZapierList = ({
    data,
    isLoading,
    sort,
    setSort,
    viewType,
    keyParams
}) => {
    const [zapierDetails, setZapierDetails] = useState({
        show: false,
        data: null
    });

    const PasswordCell = ({ value, idx }) => {
        const [isVisible, setIsVisible] = useState(false);
        const [isCopied, setIsCopied] = useState(false);

        const toggleVisibility = () => setIsVisible(!isVisible);

        const copyToClipboardHandler = async () => {
            const success = await copyToClipboard(value);
            if (success) {
                setIsCopied(true);
                message.success("Password copied to clipboard");
                setTimeout(() => setIsCopied(false), 2000);
            } else {
                message.error("Failed to copy password");
            }
        };

        return (
            <div className="d-flex justify-content-between align-items-center">
                <span style={!isVisible ? { filter: "blur(5px)", transition: "all 0.3s" } : { transition: "all 0.3s" }}>
                    {value}
                </span>
                <div className="d-flex align-items-center">
                    {isVisible ? (
                        <EyeInvisibleOutlined
                            className="text-green-500 cursor-pointer mx-2 text-lg"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleVisibility();
                            }}
                            title="Hide Password"
                        />
                    ) : (
                        <EyeOutlined
                            className="text-green-500 cursor-pointer mx-2 text-lg"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleVisibility();
                            }}
                            title="Show Password"
                        />
                    )}
                    {isCopied ? (
                        <Tag color="green">Copied</Tag>
                    ) : (
                        <CopyOutlined
                            className="text-success cursor-pointer text-lg"
                            title="Copy Password"
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboardHandler();
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    const EmailCell = ({ value }) => {
        const [isCopied, setIsCopied] = useState(false);

        const copyToClipboardHandler = async () => {
            const success = await copyToClipboard(value);
            if (success) {
                setIsCopied(true);
                message.success("Email copied to clipboard");
                setTimeout(() => setIsCopied(false), 2000);
            } else {
                message.error("Failed to copy email");
            }
        };

        return (
            <div className="d-flex justify-content-between align-items-center">
                <span>{value}</span>
                {isCopied ? (
                    <Tag color="green" className="ml-2">Copied</Tag>
                ) : (
                    <CopyOutlined
                        className="text-success cursor-pointer mx-2 text-lg"
                        title="Copy Email"
                        onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboardHandler();
                        }}
                    />
                )}
            </div>
        );
    };

    const columns = [
        {
            title: "Name",
            dataIndex: ["userDetails", "name"],
            key: "name",
            render: (text, record) => (
                <span className="font-weight-bold">{record?.userDetails?.name || '-'}</span>
            ),
            isAvatar: true
        },
        {
            title: "Email",
            dataIndex: ["userDetails", "email"],
            key: "email",
        },
        {
            title: "Phone",
            dataIndex: ["userDetails", "phone"],
            key: "phone",
        },
        {
            title: "Zapier Email",
            dataIndex: "email",
            key: "zapierEmail",
            render: (text) => <EmailCell value={text} />
        },
        {
            title: "Zapier Password",
            dataIndex: "password",
            key: "password",
            render: (text, record, index) => <PasswordCell value={text} idx={index} />
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (text) => formatDateTime(text)
        },
        {
            title: "Status",
            dataIndex: "zapCreatedByAdmin",
            key: "status",
            render: (isCompleted) => (
                <Badge color={isCompleted ? "success" : "warning"}>
                    {isCompleted ? "Completed" : "Pending"}
                </Badge>
            )
        }
    ];

    return (
        <>
            {data && data.length > 0 ? (
                <>
                    {viewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={data}
                            isLoading={isLoading}
                            sort={sort}
                            setSort={setSort}
                            onRowClick={(record) =>
                                setZapierDetails({ show: true, data: record })
                            }
                        />
                    ) : (
                        <CommonCardView>
                            {data.map((record) => (
                                <EnhanceZapierCardView
                                    key={record._id}
                                    record={record}
                                    actions={[]}
                                    onCardClick={(record) => setZapierDetails({ show: true, data: record })}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No Zapier accounts found." />
            )}

            {zapierDetails.show && (
                <CommonDrawer
                    open={zapierDetails.show}
                    setOpen={() => setZapierDetails({ show: false, data: null })}
                    title="Zapier Account Details"
                    width={"1000px"}
                    children={
                        <EnhanceZapierDetails
                            zapierAccountDetail={zapierDetails.data}
                            setShowDetail={(show) => setZapierDetails(prev => ({ ...prev, show }))}
                            keyParams={keyParams}
                        />
                    }
                />
            )}
        </>
    );
};

export default EnhancedZapierList;